// ═══════════════════════════════════════════════════════════════════
//  telemetryFlush — Ship buffered events to Supabase.event_log.
// ═══════════════════════════════════════════════════════════════════
//
//  Pulls events from search + minimap subsystems via their `take*`
//  helpers and inserts them in a single batch. Errors swallowed (with
//  a console.warn) — telemetry is best-effort, never blocks the user.
//
//  Used by `useTelemetryFlush` which schedules calls on a 30-second
//  interval and on `beforeunload`.
//
// ═══════════════════════════════════════════════════════════════════

import type { SupabaseClient } from '@supabase/supabase-js';
import { takeSearchTelemetry } from './search/telemetry';
import { takeMinimapTelemetry } from './minimap/telemetry';

interface EventLogRow {
  user_id: string | null;
  subsystem: string;
  kind: string;
  payload: Record<string, unknown>;
}

const MAX_BATCH = 500;

export async function flushTelemetryToSupabase(
  client: SupabaseClient,
  userId: string | null,
): Promise<{ inserted: number; failed: number }> {
  const rows: EventLogRow[] = [];

  for (const ev of takeSearchTelemetry()) {
    rows.push({
      user_id: userId,
      subsystem: 'search',
      kind: ev.kind,
      payload: stripKind(ev),
    });
  }
  for (const ev of takeMinimapTelemetry()) {
    rows.push({
      user_id: userId,
      subsystem: 'minimap',
      kind: ev.kind,
      payload: stripKind(ev),
    });
  }

  if (rows.length === 0) return { inserted: 0, failed: 0 };

  // Insert in chunks to bound a single round-trip.
  let inserted = 0;
  let failed = 0;
  for (let i = 0; i < rows.length; i += MAX_BATCH) {
    const chunk = rows.slice(i, i + MAX_BATCH);
    const { error } = await client.from('event_log').insert(chunk);
    if (error) {
      failed += chunk.length;
       
      console.warn('[telemetry-flush]', error.message);
    } else {
      inserted += chunk.length;
    }
  }
  return { inserted, failed };
}

function stripKind(ev: Record<string, unknown>): Record<string, unknown> {
   
  const { kind: _kind, ...rest } = ev;
  return rest;
}
